// src/components/toolkit/toolStorage.js — where a tool's own work is kept.
//
// One key per tool, on this device. Everything is wrapped: a private window or
// blocked site data must leave the tool working, just without the last answer.
//
// Each entry carries __updatedAt so the hub sync can tell which copy is newer
// without merging two versions of somebody's interview sheet field by field.
// Saving notifies the sync service, which debounces the actual network write.
const keyFor = (toolId) => `story-threads-vibe_tool_${toolId}`;

let onSave = null;

/** The sync service registers here. Returns an unregister. */
export const registerToolSyncHook = (fn) => {
  onSave = typeof fn === 'function' ? fn : null;
  return () => {
    if (onSave === fn) onSave = null;
  };
};

/** The stored entry, stamp and all. Used by hydration and the sync push. */
export const loadToolEntry = (toolId) => {
  try {
    const raw = localStorage.getItem(keyFor(toolId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/** Write an entry exactly as given, stamp included. Hydration uses this. */
export const saveToolEntry = (toolId, entry) => {
  try {
    localStorage.setItem(keyFor(toolId), JSON.stringify(entry));
    return true;
  } catch {
    return false;
  }
};

/** What a tool reads: its own shape, without the stamp. */
export const loadTool = (toolId, fallback) => {
  const entry = loadToolEntry(toolId);
  if (!entry) return fallback;
  const { __updatedAt, ...data } = entry;
  return { ...fallback, ...data };
};

/** What a tool writes. Stamped now, and the sync service is told. */
export const saveTool = (toolId, value) => {
  const written = saveToolEntry(toolId, { ...value, __updatedAt: Date.now() });
  if (written && onSave) {
    try {
      onSave(toolId);
    } catch {
      /* sync is best-effort and must never break a save */
    }
  }
  return written;
};

export const hasSavedWork = (toolId) => {
  try {
    return Boolean(localStorage.getItem(keyFor(toolId)));
  } catch {
    return false;
  }
};
